import { Button, Icon, Modal, Spinner, Typography } from "@/components/atoms";
import { ABOVE_VEIVO_WALK_AROUND } from "@/helpers/veivoOverlay";
import { clsx } from "@/utils";
import { DraftChecklistItem } from "./steps";
import { VehicleCard } from "./VehicleCard";

/**
 * "Save and finish later?" (DEV-1904): what is saved, what is next — the
 * walk-around needs the phone at the car — and where to find the draft again.
 */
export const FinishLaterModal = ({
  open,
  title,
  subtitle,
  items,
  nextHint,
  saving,
  onKeepGoing,
  onFinishLater,
}: {
  open: boolean;
  title: string;
  subtitle: string;
  items: DraftChecklistItem[];
  nextHint: string;
  saving: boolean;
  onKeepGoing: () => void;
  onFinishLater: () => void;
}) => (
  <Modal open={open} onClose={onKeepGoing} className="w-[85vw] !p-6 md:w-[440px] lg:w-[440px]" zIndexClassName={ABOVE_VEIVO_WALK_AROUND}>
    <div className="flex flex-col gap-4">
      <div>
        <Typography variant="title">Save and finish later?</Typography>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          Everything so far is saved. The vehicle won&apos;t take bookings until all 5 steps are done.
        </Typography>
      </div>

      <VehicleCard title={title} subtitle={subtitle} />

      <ol className="flex flex-col gap-2" aria-label="Steps">
        {items.map((item) => (
          <li key={item.step} className="flex items-center gap-3">
            {item.state === "done" ? (
              <Icon name="TickCircle" variant="Bold" size="md" className="text-success" />
            ) : (
              <span
                aria-hidden
                className={clsx(
                  "h-5 w-5 rounded-full border-2",
                  item.state === "next" ? "border-primary bg-white ring-4 ring-inset ring-primary" : "border-neutral-mid-gray"
                )}
              />
            )}
            <Typography
              variant={item.state === "next" ? "action" : "paragraph"}
              className={clsx("flex-1 whitespace-nowrap", item.state === "todo" && "text-neutral-dark-gray")}
            >
              {item.step} · {item.label}
            </Typography>
            {item.state === "done" && (
              <Typography variant="small" className="text-success-dark">
                Saved
              </Typography>
            )}
            {item.state === "next" && (
              <Typography variant="small" className="text-right text-primary">
                Next — {nextHint}
              </Typography>
            )}
          </li>
        ))}
      </ol>

      <Typography variant="small" className="text-neutral-dark-gray">
        You&apos;ll find it at the top of Vehicles with a &apos;Continue&apos; button — it picks up right here.
      </Typography>

      <div className="flex flex-col gap-3">
        <Button variant="primary" size="lg" className="w-full" onClick={onKeepGoing}>
          Keep going
        </Button>
        <Button variant="secondary" size="lg" className="w-full" disabled={saving} onClick={onFinishLater}>
          {saving ? <Spinner /> : "Save and finish later"}
        </Button>
      </div>
    </div>
  </Modal>
);
